import { useState } from 'react'
import {
  Settings, User, ChevronDown, Workflow, Plug, FileBarChart,
  Home, FolderOpen, Save, Play, HelpCircle, Wrench, HardDrive, FileText, Activity, Sparkles, Binary, TrendingUp, Brain,
  LayoutGrid,
} from 'lucide-react'
import { isQA } from '../environment'
import './Navbar.css'

function Navbar({ mode, onModeChange, project, onBackToLanding, onOpenSettings, userProfile = {}, onRunWorkflow, onSaveWorkflow, onOpenS3Browser, onOpenHelp }) {
  const [showUserMenu,  setShowUserMenu]  = useState(false)
  const [showToolsMenu, setShowToolsMenu] = useState(false)
  const [showMoreMenu,  setShowMoreMenu]  = useState(false)

  // Get short name for navbar (e.g., "Tim H.")
  const getShortName = () => {
    if (userProfile.firstName && userProfile.lastName) {
      return `${userProfile.firstName} ${userProfile.lastName.charAt(0)}.`
    }
    return userProfile.displayName || 'User'
  }

  return (
    <nav className="navbar">
      <div className="navbar-left">
        {/* Logo - Click to go back to landing */}
        <button className="logo" onClick={onBackToLanding} title="Back to Home">
          <span className="logo-text">RecodeIT</span>
        </button>

        {/* Project selector */}
        {project && (
          <button className="project-selector">
            <FolderOpen size={16} className="project-icon" />
            <span className="project-account">{project.account}</span>
            <span className="project-separator">/</span>
            <span className="project-name">{project.name}</span>
            <ChevronDown size={14} />
          </button>
        )}
      </div>

      <div className="navbar-center">
        {/* Mode Switcher */}
        <div className="mode-switcher">
          <button
            className="mode-btn"
            onClick={onBackToLanding}
            title="Home"
          >
            <Home size={16} />
            <span>Home</span>
          </button>
          <button
            className={`mode-btn ${mode === 'workflow' ? 'active' : ''}`}
            onClick={() => onModeChange('workflow')}
            title="Workflow Designer"
          >
            <Workflow size={16} />
            <span>Workflow</span>
          </button>
          {/* MCP — hidden for now, re-enable when ready
          <button
            className={`mode-btn ${mode === 'mcp' ? 'active' : ''}`}
            title="Coming soon"
            aria-disabled="true"
            disabled
          >
            <Plug size={16} />
            <span>MCP</span>
          </button>
          */}
          {/* Reports button — hidden for now (not needed on workflow navbar)
          <button
            className="mode-btn"
            onClick={() => window.open(window.location.origin + window.location.pathname + '?standalone=reports', '_blank')}
            title="Reports (opens in new tab)"
          >
            <FileBarChart size={16} />
            <span>Reports</span>
          </button>
          */}

          {/* ── More menu (HITL flow) — hidden for now (customer questions, needs revisit) ── */}
          {false && (
          <div className="more-menu-container">
            <button
              className={`mode-btn more-trigger ${showMoreMenu ? 'active' : ''}`}
              onClick={() => setShowMoreMenu(v => !v)}
              disabled={false}
              title="More"
            >
              <LayoutGrid size={15} />
              <ChevronDown size={12} className={`more-chevron ${showMoreMenu ? 'open' : ''}`} />
            </button>

            {showMoreMenu && (
              <>
                {/* Transparent backdrop — clicking outside closes the menu */}
                <div className="more-backdrop" onClick={() => setShowMoreMenu(false)} />

                <div className="more-dropdown">
                  <div className="dropdown-section-label">MODERNIZATION FLOW</div>
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onModeChange('guided-analyze') }}>
                    <span>Analyze</span>
                  </button>
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onModeChange('generate-docs') }}>
                    <span>Generate Documentation</span>
                  </button>
                  <div className="dropdown-divider" />
                  {/* EBL disabled — graph + AIL replaces it (see docs/20260721_define_services_architecture.md)
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onModeChange('extract-biz') }}>
                    <span>Extract Business Logic</span>
                  </button>
                  */}
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onModeChange('decompose-code') }}>
                    <span>Define Services</span>
                  </button>
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onModeChange('refactor-code') }}>
                    <span>Refactor Code</span>
                  </button>
                  <button className="dropdown-item" disabled title="Coming soon">
                    <span>Refine Code</span>
                  </button>
                  <button className="dropdown-item" disabled title="Coming soon">
                    <span>Build / Package</span>
                  </button>
                  <div className="dropdown-divider" />
                  <div className="dropdown-section-label">OUTPUT</div>
                  <button className="dropdown-item" onClick={() => {
                    setShowMoreMenu(false)
                    window.open(window.location.origin + window.location.pathname + '?standalone=reports', '_blank')
                  }}>
                    <span>Reports</span>
                  </button>
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onOpenSettings?.() }}>
                    <span>Settings</span>
                  </button>
                </div>
              </>
            )}
          </div>
          )}

        </div>
      </div>

      <div className="navbar-right">
        {/* Quick actions - only show in workflow mode */}
        {mode === 'workflow' && (
          <>
            <div className="quick-actions">
              <button
                className="icon-btn"
                onClick={onRunWorkflow}
                disabled={!project}
                title={project ? "Run Workflow" : "Select a project first"}
              >
                <Play size={18} />
              </button>
              <button
                className="icon-btn"
                onClick={onSaveWorkflow}
                disabled={!project}
                title={project ? "Save Workflow" : "Select a project first"}
              >
                <Save size={18} />
              </button>
            </div>

            <div className="navbar-divider" />
          </>
        )}

        {/* Tools menu — hidden for now (customer questions, needs revisit) */}
        {false && (
        <div className="tools-menu-container">
          <button
            className="icon-btn"
            onClick={() => setShowToolsMenu(!showToolsMenu)}
            title="Tools"
          >
            <Wrench size={18} />
          </button>

          {showToolsMenu && (
            <>
              <div className="more-backdrop" onClick={() => setShowToolsMenu(false)} />
              <div className="tools-dropdown">
                <div className="dropdown-section-label">TOOLS</div>
                <button className="dropdown-item" onClick={() => { setShowToolsMenu(false); onModeChange('generate-docs') }}>
                  <FileText size={16} />
                  <span>Generate Documentation</span>
                </button>
                <button className="dropdown-item" onClick={() => { setShowToolsMenu(false); onModeChange('model-training') }}>
                  <Brain size={16} />
                  <span>Model Training</span>
                </button>
              </div>
            </>
          )}
        </div>
        )}

        <button className="icon-btn" onClick={onOpenSettings} title="Settings">
          <Settings size={18} />
        </button>
        <button className="icon-btn" onClick={onOpenHelp} title="Help & Guidance">
          <HelpCircle size={18} />
        </button>

        {/* User menu */}
        <div className="user-menu-container">
          <button
            className="user-btn"
            onClick={() => setShowUserMenu(!showUserMenu)}
          >
            <div className="user-avatar">
              <User size={16} />
            </div>
            <span>{getShortName()}</span>
            <ChevronDown size={14} />
          </button>

          {showUserMenu && (
            <div className="user-dropdown">
              <div className="dropdown-header">
                <span className="user-name">{userProfile.displayName || 'User'}</span>
                <span className="user-email">{userProfile.email || 'No email set'}</span>
              </div>
              <div className="dropdown-divider" />
              <button className="dropdown-item" onClick={onOpenSettings}>
                <Settings size={16} />
                <span>Settings</span>
              </button>
              <button className="dropdown-item" onClick={onBackToLanding}>
                <Home size={16} />
                <span>Back to Home</span>
              </button>
              <div className="dropdown-divider" />
              <button className="dropdown-item danger">
                <span>Sign Out</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </nav>
  )
}

export default Navbar
